import React, { useEffect, useState } from "react";
import { Button, Input } from "antd";
import { env } from "../../env/env";
import ReCAPTCHA from "react-google-recaptcha";
import { isValidEmail, isValidPassword } from "../../util/utils";
import { Button as CButton } from "../account/misc/Button";
import { LeftOutlined } from "@ant-design/icons";
import Tos from "./Tos";
import { LogRegLogo } from "../util/LogRegLogo";
import StepCounter from "../StepCounter";
import { RegisterMemberDto, SsoMember } from "../../generated/dashboard-client";
import { Spinner } from "../account/misc/Spinner";
import { ReactComponent as MicrosoftIcon } from "./icons/microsoft.svg";
import mailImg from "./icons/mail.png";
import { useMicrosoftAuth } from "../login/MicrosoftAuthButton";

interface RegStepTwoProps {
  inviteMode: boolean;
  drdFlow: boolean;
  goBack: () => void;
  onNext: (member: RegisterMemberDto) => void;
  onSignIn: () => void;
  linkExpired: boolean;
  loading: boolean;
  googleRegister: boolean;
  regStep: number;
  regStepTotal: number;
  first: string;
  last: string;
  email: string;
  password: string;
  drdCommunity?: string;
  drdCorporateGroupName?: string;
  onMicrosoftInviteSuccess?: (member: SsoMember) => void;
  onMicrosoftError?: (message: string) => void;
  onStartLoading?: () => void;
  microsoftError?: string;
}

export default function RegStepFields({
                                     inviteMode,
                                     drdFlow,
                                     goBack,
                                     onNext,
                                     onSignIn,
                                     linkExpired,
                                     loading,
                                     googleRegister,
                                     regStep,
                                     regStepTotal,
                                     first,
                                     last,
                                     email,
                                     password,
                                     drdCommunity,
                                     drdCorporateGroupName,
                                     onMicrosoftInviteSuccess,
                                     onMicrosoftError,
                                     onStartLoading,
                                     microsoftError
                                   }: RegStepTwoProps) {

  const { loginWithMicrosoft } = useMicrosoftAuth({
    onSuccess: (m) => onMicrosoftInviteSuccess?.(m),
    onStartLoading: () => onStartLoading?.(),
    onError: (msg) => onMicrosoftError?.(msg)
  });

  const [firstName, setFirstName] = useState(first);
  const [lastName, setLastName] = useState(last);
  const [emailAddress, setEmailAddress] = useState(email);
  const [community, setCommunity] = useState(drdCommunity || '');
  const [corporateGroupName, setCorporateGroupName] = useState(drdCorporateGroupName || '');
  const [pwd, setPwd] = useState(password);
  const [confirm, setConfirm] = useState(password);
  const [captcha, setCaptcha] = useState(false);
  const [inviteEmailChosen, setInviteEmailChosen] = useState(false);
  const [drdEmailChosen, setDrdEmailChosen] = useState(false);

  const [isFirstErr, setFirstErr] = useState(false);
  const [isLastErr, setLastErr] = useState(false);
  const [isEmailErr, setEmailErr] = useState(false);
  const [isPassErr, setPassErr] = useState(false);
  const [isConfirmErr, setConfirmErr] = useState(false);

  const [isValid, setValid] = useState(false);

  useEffect(() => {
    let valid = true;
    if (!captcha || isPassErr || isConfirmErr || isEmailErr) {
      valid = false;
    }

    if (inviteMode) {
      if (!firstName || !lastName || pwd !== confirm) {
        valid = false;
      }
    } else {
      if (!firstName?.trim() || !lastName?.trim() || !emailAddress?.trim() || !pwd?.trim() || !isValidEmail(emailAddress)) {
        valid = false;
      }
    }

    setValid(valid);
  }, [inviteMode, captcha, firstName, lastName, emailAddress, pwd, confirm, isPassErr, isConfirmErr, isEmailErr]);

  function onSubmit() {
    if (isValid) {
      onNext({
        first: firstName.trim(),
        last: lastName.trim(),
        email: emailAddress.trim(),
        password: pwd.trim(),
        drdCommunity: community.trim(),
        drdCorporateGroupName: corporateGroupName.trim()
      });
    }
  }

  function onPassChange(password: string) {
    setPwd(password.trim());
    setPassErr(!isValidPassword(password.trim()));
    setConfirmErr(password.trim() !== confirm.trim());
  }

  function onConfirmChange(confirm: string) {
    setConfirm(confirm.trim());
    setConfirmErr(pwd.trim() !== confirm.trim());
  }

  function onEmailChange(email: string) {
    setEmailAddress(email);
    if (isValidEmail(email)) {
      setEmailErr(false);
    }
  }
  function onCommunityChange(community: string) {
    setCommunity(community);
  }
  function onCorporateGroupNameChange(name: string) {
    setCorporateGroupName(name);
  }

  const headerText = inviteMode ? "Enter your information" : drdFlow ? "Finish setting up your account" : "Create your account";

  if (!loading && inviteMode && !inviteEmailChosen && !linkExpired) {
    return (
      <div className={"step-type step full"}>
        <LogRegLogo />
        <h2>Join your team</h2>
        <h5>Use your email or Microsoft account to continue with MustHaveMenus.</h5>
        {microsoftError && (
          <p className="sso-error" style={{color: "#ff4d4f", textAlign: "center", marginBottom: 16}}>{microsoftError}</p>
        )}
        <div className="login-buttons">
          <div onClick={() => loginWithMicrosoft()}>
            <MicrosoftIcon />
            <span className={"login-option"}>Continue with Microsoft</span>
          </div>
          <div onClick={() => setInviteEmailChosen(true)}>
            <img src={mailImg} alt="Email" />
            <span className={"login-option"}>Continue with Email</span>
          </div>
        </div>
        <Tos />
      </div>
    );
  }

  if (!loading && drdFlow && !drdEmailChosen && !linkExpired) {
    return (
      <div className={"step-fields step full"}>
        <div className={"form"}>
          <StepCounter total={regStepTotal} current={regStep} />
          <h2>{headerText}</h2>
          <h6>Already have an account? <CButton type={"LinkPrimary"} className={"sign-in-link"} onClick={onSignIn}>Sign in</CButton></h6>

          <div className={"blk"}>
            <Input type={"text"} placeholder={"Community"} value={community} size={"large"}
                   onChange={(e) => onCommunityChange(e.target.value)}/>
          </div>
          <div className={"blk"}>
            <Input type={"text"} placeholder={"Corporate Group Name"} value={corporateGroupName} size={"large"}
                   onChange={(e) => onCorporateGroupNameChange(e.target.value)}/>
          </div>

          {microsoftError && (
            <p className="sso-error" style={{color: "#ff4d4f", textAlign: "center", marginBottom: 16}}>{microsoftError}</p>
          )}
          <div className="login-buttons">
            <div onClick={() => loginWithMicrosoft(
              `/account/register?source=drd&community=${encodeURIComponent(community.trim())}&corporate=${encodeURIComponent(corporateGroupName.trim())}`
            )}>
              <MicrosoftIcon />
              <span className={"login-option"}>Continue with Microsoft</span>
            </div>
            <div onClick={() => setDrdEmailChosen(true)}>
              <img src={mailImg} alt="Email" />
              <span className={"login-option"}>Continue with Email</span>
            </div>
          </div>
          <Tos />
        </div>
      </div>
    );
  }

  return (
    loading ? <div className={"spinner"}><Spinner /></div> : <div className={"step-fields step full"}>
      {!inviteMode && !drdFlow && <button className={"back"} onClick={goBack}><LeftOutlined /></button>}
      {inviteMode && inviteEmailChosen && <button className={"back"} onClick={() => setInviteEmailChosen(false)}><LeftOutlined /></button>}
      {drdFlow && drdEmailChosen && <button className={"back"} onClick={() => setDrdEmailChosen(false)}><LeftOutlined /></button>}

      {linkExpired && (
        <div className={"form expired"}>
          <LogRegLogo />
          <h1 className="emoji">&#128532;</h1>
          <h1>
            <b>Oh no! This link is expired.</b>
          </h1>
        </div>
      )}

      {!linkExpired && (
        <div className={"form"}>
          <StepCounter total={regStepTotal} current={regStep} />
          <h2>{headerText}</h2>
          {!inviteMode &&
            <h6>Already have an account? <CButton type={"LinkPrimary"} className={"sign-in-link"} onClick={onSignIn}>Sign in</CButton></h6>}

          <div className={"form"}>
            {!googleRegister && <div className={!inviteMode ? "group" : ""}>
              <div className={"blk"}>
                <Input type={"text"} placeholder={"First Name"} value={firstName} size={"large"}
                       className={isFirstErr ? "err" : ""}
                       onChange={ev => {
                         setFirstName(ev.target.value);
                         setFirstErr(!ev.target.value.trim());
                       }} />
                {isFirstErr && <label className={"err"}>Enter your first name.</label>}
              </div>
              <div className={"blk"}>
                <Input type={"text"} placeholder={"Last Name"} value={lastName} size={"large"}
                       className={isLastErr ? "err" : ""}
                       onChange={ev => {
                         setLastName(ev.target.value);
                         setLastErr(!ev.target.value.trim());
                       }} />
                {isLastErr && <label className={"err"}>Enter your last name.</label>}
              </div>
            </div>}

            {!inviteMode && !googleRegister && <div className={"blk"}>
              <Input type={"email"} placeholder={"Email"} value={emailAddress} size={"large"}
                     className={isEmailErr ? "err" : ""}
                     onChange={(e) => onEmailChange(e.target.value)}
                     onBlur={ev => setEmailErr(!isValidEmail(ev.target.value.trim()))} />
              {isEmailErr && <label className={"err"}>Enter a valid email address.</label>}
            </div>}

            {drdFlow && <div className={"blk"}>
              <Input type={"text"} placeholder={"Community"} value={community} size={"large"}
                     onChange={(e) => onCommunityChange(e.target.value)}/>
            </div>}

            {drdFlow && <div className={"blk"}>
              <Input type={"text"} placeholder={"Corporate Group Name"} value={corporateGroupName} size={"large"}
                     onChange={(e) => onCorporateGroupNameChange(e.target.value)}/>
            </div>}

            {!googleRegister && <div className={"group vertical"}>
              <div>
                <div className={"blk"}>
                  <Input.Password type={"password"} placeholder={"Password"} value={pwd} size={"large"}
                                  onChange={ev => onPassChange(ev.target.value)} />
                </div>

                <div className={"blk"}>
                  <Input.Password type={"password"} placeholder={"Retype Password"} value={confirm} size={"large"}
                                  onChange={ev => onConfirmChange(ev.target.value)} />
                </div>
              </div>
              <div className={"msg"}>
                <label className={`pass ${isPassErr ? "err" : ""}`}>8+ characters with a mix of letters, numbers and symbols (no \ or ").</label>
                {isConfirmErr && <label className={`pass err`}>Your passwords don't match.</label>}
              </div>
            </div>}

            <div className={"blk recaptcha"}>
              <ReCAPTCHA size="normal" sitekey={env.reCaptchaKey} onChange={() => setCaptcha(true)} />
            </div>
          </div>

          <footer>
            <Tos />
            <Button
              size={"large"}
              className={"primary"}
              onClick={onSubmit}
              disabled={!isValid}>
              {drdFlow ? 'Create My Account' : 'Next'}
            </Button>
          </footer>
        </div>
      )}
    </div>
  );
}
